/* globals Blob */
/* globals File */
import React, { Fragment, useState, useRef, useEffect } from 'react'
import { useSelector } from 'react-redux'
import flowRight from 'lodash/flowRight'
import get from 'lodash/get'
import { useParams } from 'react-router'
import { useHistory } from 'react-router-dom'

import { toast } from '@fielded/shared-ui/src/components/Page'
import { Text } from '@fielded/shared-ui'

import { withApi } from '../../../../../common/ApiProvider'
import withConfig from '../../../../../van-shared/hoc/withConfig'
import { withUser } from '../../../../../common/AuthenticationProvider'
import { selectUserCountry, selectUserLocation } from '../../../../../root/reducers/common/location'
import { getErrorListItems, getFormattedValue, transformErrorMessage, isExcelFile, generateOutBoundExcelTemplate, generateOutBoundCSVTemplate } from './utils'
import PSMShipmentExportImport from './PSMShipmentExportImport'
import { hasFeature } from '../../../../../van-shared/utils/features'

const CustomListWrapper = ({ errorMessage, children }) => (
  <Fragment>
    {errorMessage && <Text>{errorMessage}</Text>}
    <ul className='vs-u-padding'>
      {children}
    </ul>
  </Fragment>
)

const onHandleError = (err) => {
  const feedback = {
    title: 'File Upload Error',
    type: 'warning',
    children: null,
    autoDismiss: 10000
  }

  // When the error is an object
  if (err.error || err.message) {
    const errorMessage = err.error || err.message
    feedback.children = <Text>{errorMessage}</Text>

    if (err.locationIds || err.productIds) {
      const errorReasons = err.locationIds || err.productIds
      if (errorReasons.length) {
        feedback.children = (
          <CustomListWrapper errorMessage={transformErrorMessage(errorMessage, errorReasons)}>
            {getErrorListItems(errorReasons).map(reason => <li key={reason}>{reason}</li>)}
          </CustomListWrapper>
        )
      }
    }

    if (err.locations && err.locations.length) {
      const errorReasons = err.locations
      feedback.children = (
        <CustomListWrapper errorMessage={transformErrorMessage(errorMessage, errorReasons)}>
          {getErrorListItems(errorReasons).map(reason => (
            <li key={reason.supplier}>
              {reason.supplier}
              <CustomListWrapper>
                {reason.locationIds.map(locationId => (
                  <li key={locationId}>{locationId}</li>
                ))}
              </CustomListWrapper>
            </li>
          ))}
        </CustomListWrapper>
      )
    }

    if (err.shipments && err.shipments.length) {
      const errorReasons = err.shipments
      feedback.children = (
        <CustomListWrapper errorMessage={transformErrorMessage(errorMessage, errorReasons)}>
          {getErrorListItems(errorReasons).map(reason => (
            <li key={reason.origin}>
              {reason.origin} <br />
              {reason.destination}
              <CustomListWrapper>
                {reason.externalIds.map(externalId => (
                  <li key={externalId}>{externalId}</li>
                ))}
              </CustomListWrapper>
            </li>
          ))}
        </CustomListWrapper>
      )
    }
  }

  // When the error is an array
  if (err.errors) {
    const errorInfoList = []

    for (const errorItem of err.errors) {
      const affectedRows = errorItem.rowsAffected
      const rowMessage = affectedRows.length > 5
        ? `${getErrorListItems(affectedRows, 5).join(', ')}... and ${getFormattedValue(affectedRows.length - 5)} more`
        : affectedRows.length

      const errorInfo = `${errorItem.error}. Affected rows: ${rowMessage}`
      errorInfoList.push(errorInfo)
    }

    feedback.children = (
      <CustomListWrapper>
        {errorInfoList.map(info => <li key={info}>{info}</li>)}
      </CustomListWrapper>
    )
  }

  toast(feedback)
}

const PSMShipmentExportImportContainer = ({ api, user, config }) => {
  const hasToAddRoutesToShipments = hasFeature(config.features, 'outboundShipments:addRoutesToShipments')

  const country = useSelector(selectUserCountry) || get(user, 'location.country')
  const [isExcelTemplateDownloading, setIsExcelTemplateDownloading] = useState(false)
  const [isCSVTemplateDownloading, setIsCSVTemplateDownloading] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [warehouseCode, setWarehouseCode] = useState()
  const [facilitiesExamples, setFacilitiesExamples] = useState([])
  const [productExamples, setProductExamples] = useState([])
  const [locationObject, setLocationObject] = useState(useSelector(selectUserLocation))
  const documentFormRef = useRef()
  const history = useHistory()
  const { view } = useParams()
  const mainPathname = history.location.pathname.replace(`/${view}`, '')

  const initialize = async () => {
    // We get the real data about the location and related facilities to make sure the user can see the relevant data in the example table.
    try {
      const locationId = get(user, 'location.id')
      const userPrograms = get(user, 'programs', [])

      let resolvedLocation = locationObject
      if (!resolvedLocation) {
        resolvedLocation = await api.location.get(locationId)
        setLocationObject(resolvedLocation)
      }

      const [productsList, relatedLocations] = await Promise.all([
        api.product.listForPrograms(userPrograms),
        api.location.listRelated(locationId, { includeSelf: false })
      ])

      const currentWarehouseCode = get(resolvedLocation, 'additionalData.warehouseCode')

      const relatedFacilities = relatedLocations.filter((place) => {
        const isPSMWarehouse = get(place, 'additionalData.isPSMWarehouse')
        const hasWarehouseCode = get(place, 'additionalData.warehouseCode')
        return !isPSMWarehouse && hasWarehouseCode
      })

      setWarehouseCode(currentWarehouseCode)
      setFacilitiesExamples(relatedFacilities)
      setProductExamples(productsList)
    } catch (e) {
      console.log(e)
      // We don't really want to break the experience if the data is not there
    }
  }

  useEffect(() => {
    initialize()
  }, [])

  const onUpload = async () => {
    setIsUploading(true)
    const files = documentFormRef.current.getFiles()
    let importFile = files.file[0]

    if (isExcelFile(importFile)) {
      try {
        const fileBuffer = await importFile.arrayBuffer()
        const { csvContent, csvFileName } = await api.shipment.convertExcelOutboundShipmentToCsv(fileBuffer, importFile.name)
        const csvBlob = new Blob([csvContent], { type: 'text/csv' })
        importFile = new File([csvBlob], csvFileName, { type: 'text/csv' })
      } catch (err) {
        toast({
          title: 'Excel Conversion Error',
          type: 'warning',
          children: err.message || 'Failed to convert Excel to valid CSV. Please use the provided template.'
        })
        setIsUploading(false)
        return
      }
    }

    const formData = new window.FormData()
    formData.append(importFile.name, importFile)

    try {
      const shouldSync = true
      const uploadedShipments = await api.shipment.uploadShipments(formData, hasToAddRoutesToShipments, shouldSync)

      const stockTransfers = (uploadedShipments || []).filter(i => i.planningType === 'interwarehouse')
      const lmds = (uploadedShipments || []).filter(i => i.planningType !== 'interwarehouse')
      toast({
        title: 'Shipments created successfully',
        type: 'success',
        children: <Text>Your file created {lmds.length} last-mile deliveries and {stockTransfers.length} stock transfers.</Text>
      })

      if (stockTransfers.length > 0) {
        history.push('/shipments/stock-transfers')
      } else {
        history.push('/shipments/last-mile-deliveries')
      }
    } catch (err) {
      onHandleError(err)
    } finally {
      setIsUploading(false)
    }
  }

  const onError = (error) => {
    onHandleError(error)
  }

  const onCancel = () => {
    history.goBack()
  }

  const handleExcelTemplateDownload = () => {
    generateOutBoundExcelTemplate({
      toast,
      api,
      warehouseCode,
      facilitiesExamples,
      productExamples,
      country,
      setIsExcelTemplateDownloading
    })
  }

  const handleCSVTemplateDownload = () => {
    generateOutBoundCSVTemplate({
      toast,
      warehouseCode,
      facilitiesExamples,
      productExamples,
      country,
      setIsCSVTemplateDownloading
    })
  }

  return (
    <PSMShipmentExportImport
      config={config}
      country={country}
      onUpload={onUpload}
      isUploading={isUploading}
      onError={onError}
      onCancel={onCancel}
      view={view}
      mainPathname={mainPathname}
      documentFormRef={documentFormRef}
      warehouseCode={warehouseCode}
      facilitiesExamples={facilitiesExamples}
      productExamples={productExamples}
      isExcelTemplateDownloading={isExcelTemplateDownloading}
      isCSVTemplateDownloading={isCSVTemplateDownloading}
      handleExcelTemplateDownload={handleExcelTemplateDownload}
      handleCSVTemplateDownload={handleCSVTemplateDownload}
    />
  )
}

const withHOCs = flowRight(
  withApi,
  withConfig,
  withUser
)

export default withHOCs(PSMShipmentExportImportContainer)
